export interface TabDef {
  id: string;
  label: string;
  /** Optional right-side hint: '•' has data, '…' loading, etc. */
  badge?: string;
  /** A live figure shown instead of the badge — e.g. "2/10". */
  value?: string;
  badgeColor?: string;
}

export default function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: TabDef[];
  active: string;
  onChange: (id: string) => void;
}): JSX.Element {
  return (
    <div className="w-full max-w-5xl mx-auto flex flex-wrap gap-2">
      {tabs.map((t, i) => {
        const on = t.id === active;
        return (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            // An inactive tab still has to look pressable. A "segmented" look
            // without a border left grey text on the page ground, which reads as
            // a label — or as a disabled control. The boundary is what carries
            // the affordance, so it is picked to clear 3:1 against BOTH the page
            // ground and its own fill (3.55:1 / 3.07:1) rather than by eye — the
            // edge it replaced measured 1.99:1 and genuinely disappeared.
            className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium border transition cursor-pointer active:translate-y-px ${
              on
                ? 'bg-cyan text-warm-dark border-cyan shadow-sm shadow-cyan/25'
                : 'bg-[#26262F] text-text-primary border-[#70707D] shadow-sm shadow-black/20 hover:border-cyan/70 hover:bg-[#32323E]'
            }`}
          >
            {/* Numbered to signal the order to click through after a scan (#5). */}
            <span className={`mr-2 font-mono text-xs ${on ? 'text-warm-dark/60' : 'text-text-secondary'}`}>{i + 1}</span>
            {t.label}
            {/* A value beats a dot: "2/10" says whether it is worth opening. */}
            {t.value ? (
              <span className={`ml-2 font-mono text-xs ${on ? 'text-warm-dark/70' : 'text-cyan/80'}`}>{t.value}</span>
            ) : t.badge ? (
              <span className="ml-1.5 font-mono text-xs" style={{ color: on ? undefined : (t.badgeColor ?? '#6B6B76') }}>
                {t.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
